import { styled } from '@mui/material';
import {
    ColDef,
    ColumnEventType,
    ColumnState,
    FirstDataRenderedEvent,
    GridApi,
    GridReadyEvent,
} from 'ag-grid-community';
import { AgGridReactProps } from 'ag-grid-react';
import {
    ChartSettingsT,
    ColumnSortingT,
    ColumnWidthT,
} from 'business/definitions';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import { AGGRID_DEFAULT_HEADER_HEIGHT } from 'ui/AgGridReact/constants';
import { getApplyColumnState } from 'ui/AgGridReact/helpers';
import { useDebounce } from 'hooks/helpers';
import { useBackendSyncedContext } from 'routes/Explore/components/Contexts/BackendSyncedContext/BackendSyncedContext';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import NumberFloatingFilter from 'ui/AgGridReact/NumberFloatingFilter';
import { ColDefWithField } from './defaultSettings';
import { computeAggregations } from './helpers/aggregations';
import './_tableChart.scss';

const DEBOUNCE_TIME = 1_000;

const StyContainer = styled('div', {
    name: 'StyContainer',
})({
    height: '100%',
    '& .ag-tool-panel-wrapper': {
        width: '100%',
    },
    '&&& .ag-floating-filter .MuiTextField-root': {
        alignItems: 'center',
    },
    '&&& .ag-floating-filter-body': {
        alignItems: 'center',
    },
    '&&& .ag-input-field-input': {
        height: '100%',
    },
});

type Props = {
    data: ResponseDataT[];
    colDefs: ColDefWithField[];
    defaultColDef: ColDef;
    className?: string;
    gridProps: Partial<AgGridReactProps>;
    chartSettings: ChartSettingsT;
    autoSizeColumns?: boolean;
    autoSizeHeight?: boolean;
    updateSettings: (newSettings: ChartSettingsT) => void;
};

const TableAgGrid = ({
    data,
    colDefs,
    defaultColDef,
    className,
    gridProps,
    chartSettings,
    autoSizeColumns = false,
    autoSizeHeight = false,
    updateSettings,
}: Props): JSX.Element => {
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [gridColDefs, setGridColDefs] = useState<ColDefWithField[]>(colDefs);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isUpdatingAggregationRowData, setIsUpdatingAggregationRowData] =
        useState<boolean>(false);
    const { setIsBackendSynced } = useBackendSyncedContext();

    const updateColumnSettings = (newColDefs: ColumnState[]) => {
        if (!newColDefs) return;

        const newColDefsOrder: string[] = newColDefs.map((cd) => cd.colId);

        const newHiddenColumns: string[] = newColDefs
            .filter((cd) => cd.hide)
            .map((cd) => cd.colId);

        const newColumnsSorting: ColumnSortingT[] = newColDefs
            .filter((cd) => cd.sort !== null)
            .sort((col1, col2) => (col1.sortIndex || 0) - (col2.sortIndex || 0))
            .map((cd) => ({
                columnName: cd.colId,
                direction: cd.sort || 'asc',
            }));

        const newColumnsWidth: ColumnWidthT[] = newColDefs.map((cd) => ({
            columnName: cd.colId,
            width: cd.width || 0,
        }));

        updateSettings({
            columnsSorting: newColumnsSorting,
            columnsHidden: newHiddenColumns,
            columnsWidth: newColumnsWidth,
            columnOrder: newColDefsOrder,
        });
    };

    const debouncedUpdateColumnsSettings = useDebounce(
        updateColumnSettings,
        DEBOUNCE_TIME,
    );

    const onGridReady = useCallback((params: GridReadyEvent) => {
        setGridApi(params.api);
        if (autoSizeColumns) {
            params.api.autoSizeAllColumns();
        }
        // we need this in order to update the chartSettings when we change the data
        updateColumnSettings(params.api.getColumnState());
    }, []);

    const applyColumnState = useMemo(
        () => getApplyColumnState(gridApi),
        [gridApi],
    );

    useEffect(() => {
        if (isInitialized || !gridApi) return;
        applyColumnState(colDefs);
        setIsInitialized(true);
    }, [gridApi, colDefs, isInitialized, applyColumnState]);

    const updateColumnWrapper =
        (
            updateColumnsSettings: (newColDefs: ColumnState[]) => void,
            changesMustBeSaved: boolean = true,
        ): (() => void) =>
        () => {
            if (!isInitialized) return;
            if (gridApi === null) return;

            const newColDefs = gridApi.getColumnState();
            setGridColDefs(gridApi.getColumnDefs() as ColDefWithField[]);
            updateColumnsSettings(newColDefs);

            if (changesMustBeSaved) setIsBackendSynced(false);
        };

    // The debounce is useful to avoid multiple updates for one resize
    const debouncedUpdateColumnsWidth = useDebounce(
        (source: ColumnEventType) => {
            const changesMustBeSaved = source === 'uiColumnDragged';

            const wrappedUpdateColumnsWidth = updateColumnWrapper(
                updateColumnSettings,
                changesMustBeSaved,
            );
            wrappedUpdateColumnsWidth();
        },
        DEBOUNCE_TIME,
    );

    useEffect(() => {
        if (gridApi) gridApi.resetRowHeights();
    }, [gridApi, gridProps.rowHeight]);

    useEffect(() => {
        if (gridApi) {
            const currentColDefs = gridApi.getColumnDefs();
            if (!currentColDefs) return;
            const updatedColDefs: ColDefWithField[] = [];
            currentColDefs.forEach((colDef) => {
                updatedColDefs.push({
                    ...colDef,
                    floatingFilter: chartSettings.showFilters,
                } as ColDefWithField);
            });
            gridApi.refreshHeader();
            setGridColDefs(updatedColDefs);
        }
    }, [gridApi, chartSettings.showFilters]);

    const onFirstDataRendered = (params: FirstDataRenderedEvent) => {
        setIsUpdatingAggregationRowData(true);
        if (autoSizeColumns) {
            params.api.autoSizeAllColumns();
        }
    };

    const onFilterChanged = () => setIsUpdatingAggregationRowData(true);

    const onModelUpdated = () => setIsUpdatingAggregationRowData(true);

    useEffect(() => {
        if (isUpdatingAggregationRowData && gridApi) {
            if (chartSettings.showAggregates) {
                gridApi.setGridOption(
                    'pinnedBottomRowData',
                    computeAggregations(gridApi),
                );
            } else {
                gridApi.setGridOption('pinnedBottomRowData', undefined);
            }
            setIsUpdatingAggregationRowData(false);
        }
    }, [isUpdatingAggregationRowData, chartSettings.showAggregates, gridApi]);

    return (
        <StyContainer data-testid="table-chart">
            <AgGridReact
                className={`ag-theme-material ${className || ''}`}
                suppressColumnVirtualisation
                defaultColDef={defaultColDef}
                headerHeight={AGGRID_DEFAULT_HEADER_HEIGHT}
                rowData={data}
                onGridReady={onGridReady}
                columnDefs={gridColDefs}
                onModelUpdated={onModelUpdated}
                onColumnMoved={updateColumnWrapper(
                    debouncedUpdateColumnsSettings,
                )}
                onColumnVisible={updateColumnWrapper(
                    debouncedUpdateColumnsSettings,
                )}
                onSortChanged={updateColumnWrapper(
                    debouncedUpdateColumnsSettings,
                )}
                onColumnResized={(event) =>
                    debouncedUpdateColumnsWidth(event.source)
                }
                onFilterChanged={onFilterChanged}
                onFirstDataRendered={onFirstDataRendered}
                components={{
                    textFloatingFilter: TextFloatingFilter,
                    numberFloatingFilter: NumberFloatingFilter,
                }}
                domLayout={autoSizeHeight ? 'autoHeight' : undefined}
                {...gridProps}
            />
        </StyContainer>
    );
};

export default TableAgGrid;
